import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Search, X, Loader2, Package, Layers, Tag, ArrowRight } from "lucide-react";
import axios from "../../appUtils/axiosConfig";

export default function HeaderSearchBar({ onAfterNavigate, isMobile = false }) {
  const [searchText, setSearchText] = useState("");
  const [suggestions, setSuggestions] = useState({ products: [], categories: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const BaseUrl = process.env.REACT_APP_BASE_URL || "";

  // Debounced live suggestions fetcher (products + categories)
  useEffect(() => {
    const trimmed = searchText.trim();
    if (trimmed.length < 1) {
      setSuggestions({ products: [], categories: [] });
      setIsLoading(false);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    setIsOpen(true);

    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(
          `products/get_active_products.php?page=1&limit=6&pincode=${localStorage.getItem("delivery_pincode") || ""}&search=${encodeURIComponent(
            trimmed
          )}`
        );
        const products = res?.data?.data || res?.data?.products || [];
        const categories = res?.data?.categories || [];

        setSuggestions({
          products: Array.isArray(products) ? products : [],
          categories: Array.isArray(categories) ? categories : [],
        });
      } catch (err) {
        setSuggestions({ products: [], categories: [] });
      } finally {
        setIsLoading(false);
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchText]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setIsOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleSelectProduct = (product) => {
    setIsOpen(false);
    setSearchText("");
    const targetSlug = product?.slug || product?.id;
    if (targetSlug) {
      navigate(`/product-details/${targetSlug}`);
    }
    if (onAfterNavigate) onAfterNavigate();
  };

  const handleSelectCategory = (category) => {
    setIsOpen(false);
    setSearchText("");
    const params = new URLSearchParams();
    if (category?.title) params.set("category", category.title);
    if (category?.id) params.set("category_id", category.id);
    navigate(`/products?${params.toString()}`);
    if (onAfterNavigate) onAfterNavigate();
  };

  const handleFullSearch = () => {
    const trimmed = searchText.trim();
    if (!trimmed) return;
    setIsOpen(false);
    const category = searchParams.get("category") || "";
    const categoryId = searchParams.get("category_id") || "";
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (categoryId) params.set("category_id", categoryId);
    params.set("search", trimmed);
    navigate(`/products?${params.toString()}`);
    if (onAfterNavigate) onAfterNavigate();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleFullSearch();
    }
  };

  const hasCategories = Array.isArray(suggestions.categories) && suggestions.categories.length > 0;
  const hasProducts = Array.isArray(suggestions.products) && suggestions.products.length > 0;
  const hasAnyResults = hasCategories || hasProducts;

  return (
    <div ref={searchRef} className="relative w-full">
      <div className="relative flex items-center">
        <input
          type="text"
          placeholder="Search products..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onFocus={() => {
            if (searchText.trim().length > 0) setIsOpen(true);
          }}
          onKeyDown={handleKeyPress}
          className="w-full h-10 bg-gray-100 border border-gray-200 rounded-l-md px-4 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-[#77C712] focus:border-transparent text-gray-800 placeholder-gray-400"
        />

        {/* Clear Button */}
        {searchText && (
          <button
            type="button"
            onClick={() => {
              setSearchText("");
              setSuggestions({ products: [], categories: [] });
              setIsOpen(false);
            }}
            aria-label="Clear search"
            className="absolute right-12 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={14} />
          </button>
        )}

       <button
  type="button"
  onClick={handleFullSearch}
  aria-label="Search"
  className="h-10 bg-[#77C712] hover:bg-green-600 text-white px-3 rounded-r-md transition-colors flex items-center justify-center flex-shrink-0"
>
  {isLoading ? (
    <Loader2 size={16} className="animate-spin" />
  ) : (
    <Search size={16} />
  )}
</button>
      </div>

      {/* Suggestion Dropdown Popover */}
      {isOpen && searchText.trim().length > 0 && (
        <div
          className={`absolute left-0 right-0 top-full mt-1.5 bg-white rounded-xl shadow-2xl border border-gray-100 z-[100] overflow-hidden max-h-[460px] flex flex-col transition-all duration-200 ${
            isMobile ? "w-full" : "w-full"
          }`}
        >
          {isLoading ? (
            <div className="p-5 flex flex-col items-center justify-center gap-2 text-gray-500">
              <Loader2 className="w-6 h-6 text-[#77C712] animate-spin" />
              <span className="text-xs font-medium">Searching products & categories...</span>
            </div>
          ) : hasAnyResults ? (
            <>
              {/* Categories Section */}
              {hasCategories && (
                <div className="bg-gray-50/80 border-b border-gray-100">
                  <div className="px-3.5 py-1.5 flex items-center gap-1.5 text-[11px] text-gray-500 font-bold uppercase tracking-wider">
                    <Layers className="w-3.5 h-3.5 text-[#77C712]" />
                    <span>Categories ({suggestions.categories.length})</span>
                  </div>
                  <div className="p-2.5 pt-1 flex flex-wrap gap-1.5">
                    {suggestions.categories.map((cat) => (
                      <button
                        key={cat.id || cat.title}
                        type="button"
                        onClick={() => handleSelectCategory(cat)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white hover:bg-green-100 text-gray-800 hover:text-green-800 transition-all border border-gray-200 hover:border-green-300 shadow-sm"
                      >
                        {cat.image ? (
                          <img
                            src={`${BaseUrl}/${cat.image}`}
                            alt={cat.title || "Category"}
                            className="w-4 h-4 object-cover rounded-full"
                            loading="lazy"
                          />
                        ) : (
                          <Tag className="w-3 h-3 text-[#77C712]" />
                        )}
                        <span>{cat.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Products Section */}
              {hasProducts && (
                <div>
                  <div className="px-3.5 py-2 bg-gray-50/60 border-b border-gray-100 flex items-center justify-between text-[11px] text-gray-500 font-bold uppercase tracking-wider">
                    <span className="flex items-center gap-1.5">
                      <Package className="w-3.5 h-3.5 text-[#77C712]" />
                      <span>Products ({suggestions.products.length})</span>
                    </span>
                  </div>

                  <div className="overflow-y-auto divide-y divide-gray-100 max-h-[280px]">
                    {suggestions.products.map((product) => {
                      const hasImage = Boolean(product?.main_image);
                      const inventories = Array.isArray(product?.inventories)
                        ? product.inventories
                        : null;
                      const hasInventoriesArray = inventories !== null;

                      let isOutOfStock = false;
                      if (hasInventoriesArray) {
                        if (inventories.length === 0) {
                          isOutOfStock = true;
                        } else {
                          const totalStock =
                            product?.stock?.total_stock !== undefined
                              ? Number(product.stock.total_stock)
                              : null;
                          const stockInStock =
                            product?.stock?.in_stock !== undefined
                              ? Boolean(product.stock.in_stock)
                              : null;
                          const hasAnyInStockInventory = inventories.some(
                            (inv) => {
                              const inStockVal = inv?.in_stock;
                              const isInStock =
                                inStockVal === true ||
                                inStockVal === 1 ||
                                inStockVal === "1";
                              const qty = Number(inv?.stock_quantity ?? 0);
                              return isInStock && qty > 0;
                            }
                          );

                          if (
                            (totalStock !== null && totalStock <= 0) ||
                            stockInStock === false ||
                            !hasAnyInStockInventory
                          ) {
                            isOutOfStock = true;
                          }
                        }
                      } else if (product?.stock) {
                        const totalStock =
                          product.stock.total_stock !== undefined
                            ? Number(product.stock.total_stock)
                            : null;
                        const stockInStock =
                          product.stock.in_stock !== undefined
                            ? Boolean(product.stock.in_stock)
                            : null;
                        if (
                          (totalStock !== null && totalStock <= 0) ||
                          stockInStock === false
                        ) {
                          isOutOfStock = true;
                        }
                      } else if (
                        product?.stock_status === "0" ||
                        product?.stock_quantity === "0"
                      ) {
                        isOutOfStock = true;
                      }

                      const firstInStockInv = inventories?.find((inv) => {
                        const inStockVal = inv?.in_stock;
                        const isInStock =
                          inStockVal === true ||
                          inStockVal === 1 ||
                          inStockVal === "1";
                        const qty = Number(inv?.stock_quantity ?? 0);
                        return isInStock && qty > 0;
                      });
                      const firstInv = firstInStockInv || inventories?.[0];

                      const rawPrice =
                        firstInv?.selling_price ??
                        product?.price?.selling_price ??
                        product?.selling_price ??
                        product?.current_price ??
                        product?.mrp;

                      const price =
                        rawPrice !== null &&
                        rawPrice !== undefined &&
                        rawPrice !== "" &&
                        !isNaN(Number(rawPrice))
                          ? Number(rawPrice)
                          : null;

                      const rawMrp =
                        firstInv?.mrp ??
                        product?.price?.mrp ??
                        product?.mrp ??
                        product?.current_mrp;

                      const mrp =
                        rawMrp !== null &&
                        rawMrp !== undefined &&
                        rawMrp !== "" &&
                        !isNaN(Number(rawMrp))
                          ? Number(rawMrp)
                          : null;

                      const rawDiscount =
                        firstInv?.discount ??
                        product?.price?.discount ??
                        product?.discount_percent ??
                        product?.discount ??
                        product?.current_discount;

                      const discount =
                        rawDiscount !== null &&
                        rawDiscount !== undefined &&
                        rawDiscount !== "" &&
                        !isNaN(Number(rawDiscount))
                          ? Math.round(Number(rawDiscount))
                          : null;

                      if (price === null || price <= 0) {
                        isOutOfStock = true;
                      }

                      const variationTitle = firstInv?.variations?.title;

                      return (
                        <div
                          key={product?.id || product?.slug}
                          onClick={() => handleSelectProduct(product)}
                          className="p-2.5 sm:p-3 hover:bg-green-50/70 transition-colors flex items-center gap-3 cursor-pointer group text-left"
                        >
                          {/* Product Thumbnail */}
                          <div className="w-12 h-12 flex-shrink-0 bg-gray-50 border border-gray-100 rounded-lg overflow-hidden flex items-center justify-center p-1">
                            {hasImage ? (
                              <img
                                src={`${BaseUrl}/${product.main_image}`}
                                alt={product?.title || "Product"}
                                className={`w-full h-full object-contain group-hover:scale-105 transition-transform ${
                                  isOutOfStock ? "opacity-75" : ""
                                }`}
                                loading="lazy"
                                decoding="async"
                              />
                            ) : (
                              <Package className="w-6 h-6 text-gray-300" />
                            )}
                          </div>

                          {/* Product Details */}
                          <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-semibold text-gray-800 group-hover:text-[#482607] truncate leading-tight">
                              {product?.title}
                            </h4>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              {isOutOfStock ? (
                                <span className="text-[11px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-600 uppercase">
                                  Out of Stock
                                </span>
                              ) : (
                                <>
                                  {price !== null && (
                                    <span className="text-xs font-bold text-gray-900">
                                      ₹{price}
                                    </span>
                                  )}
                                  {mrp !== null && Number(mrp) > Number(price) && (
                                    <span className="text-[11px] text-gray-400 line-through">
                                      ₹{mrp}
                                    </span>
                                  )}
                                  {discount !== null && Number(discount) > 0 && (
                                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-green-100 text-green-700">
                                      {discount}% OFF
                                    </span>
                                  )}
                                  {variationTitle && (
                                    <span className="text-[10px] text-gray-400">
                                      ({variationTitle})
                                    </span>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* View all results footer */}
              <div
                onClick={handleFullSearch}
                className="p-2.5 bg-gray-50 hover:bg-gray-100 text-center border-t border-gray-100 cursor-pointer transition-colors text-xs font-semibold text-[#77C712] hover:text-green-700 flex items-center justify-center gap-1.5"
              >
                <span>View all results for "{searchText.trim()}"</span>
                <ArrowRight className="w-3.5 h-3.5" />
              </div>
            </>
          ) : (
            <div className="p-6 text-center text-gray-500">
              <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-xs font-medium text-gray-700">
                No products or categories found for "{searchText}"
              </p>
              <p className="text-[11px] text-gray-400 mt-1">
                Try searching with different keywords
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
